import { MenuGroup } from "./MenuGroup";
import { RouteEnum } from "../enums/RouteEnum";
import { JSX, useEffect } from "react";
import { useAppSelector } from "../../app/hooks";
import { useGetCertificatesQuery } from "../api/certificate";
import { useEstablishmentId } from "../utils/useEstablishmentId";
import { useIsEstablishmentManager } from "../utils/useIsEstablishmentManager";
import { useGetEstablishmentsQuery } from "../api/establishment";
import { useEmployeeId } from "../utils/useEmployeeId";
import { CertificateStatusEnum } from "../enums/CertificateStatusEnum";

interface Props {
    location: string;
    setLocation: (location: string) => void;
}

export const EstablishmentMenu = (props: Props): JSX.Element => {
    const isEstablishmentManager = useIsEstablishmentManager();
    const { isPostCreateCertificateSuccess } = useAppSelector(
        (state) => state.createCertificateModal,
    );
    const { isReviewCertificateSuccess } = useAppSelector(
        (state) => state.review,
    );
    const {
        data: certificates,
        isLoading: isCertificateDataLoading,
        refetch: refetchCertificates,
    } = useGetCertificatesQuery({
        filter: {
            establishment_id: useEstablishmentId(),
            status: CertificateStatusEnum.PENDING,
        },
    });
    const { data: establishmentData, isLoading: establishmentDataIsLoading } =
        useGetEstablishmentsQuery({
            items_per_page: 9999,
            filter: {
                employee_id: useEmployeeId(),
            },
        });
    const moreThenOneEstablishment = establishmentData
        ? establishmentData.data.length > 1
        : false;

    useEffect(() => {
        if (isPostCreateCertificateSuccess || isReviewCertificateSuccess) {
            refetchCertificates();
        }
    }, [
        isPostCreateCertificateSuccess,
        isReviewCertificateSuccess,
        refetchCertificates,
    ]);

    return (
        <>
            <MenuGroup
                label={"Algemeen"}
                menuItems={[
                    {
                        label: "Dashboard",
                        icon: "BarChart",
                        link: RouteEnum.DASHBOARD,
                    },
                    {
                        label: "Meldingen",
                        icon: "Notifications",
                        link: RouteEnum.NOTIFICATIONS,
                    },
                ]}
                location={props.location}
                setLocation={props.setLocation}
            />

            <MenuGroup
                label={"Opleidingen"}
                menuItems={[
                    {
                        label: "Kwalificaties",
                        icon: "School",
                        link: RouteEnum.QUALIFICATIONS,
                    },
                    {
                        label: "Verloopt binnenkort",
                        icon: "Clock",
                        link: RouteEnum.CERTIFICATE_EXPIRY,
                    },
                    {
                        label: "Ingepland",
                        icon: "Agenda",
                        link: RouteEnum.SCHEDULED_QUALIFICATIONS,
                    },
                    {
                        label: `Beoordelen (${certificates?.meta?.total})`,
                        icon: "List",
                        link: RouteEnum.REVIEWS,
                    },
                ]}
                location={props.location}
                setLocation={props.setLocation}
                isLoading={isCertificateDataLoading}
            />

            {isEstablishmentManager && moreThenOneEstablishment ? (
                <MenuGroup
                    label={"Mijn Vestigingen"}
                    menuItems={[
                        {
                            label: `Vestigingen (${establishmentData?.meta?.total})`,
                            icon: "Location",
                            link: RouteEnum.ESTABLISHMENTS,
                        },
                        {
                            label: "Medewerkers",
                            icon: "Person",
                            link: RouteEnum.EMPLOYEES,
                        },
                    ]}
                    location={props.location}
                    setLocation={props.setLocation}
                    isLoading={establishmentDataIsLoading}
                />
            ) : (
                <MenuGroup
                    label={"Mijn Vestiging"}
                    menuItems={[
                        {
                            label: "Medewerkers",
                            icon: "Persons",
                            link: RouteEnum.EMPLOYEES,
                        },
                        {
                            label: "Persoonsgegevens beheren",
                            icon: "Person",
                            link: RouteEnum.DATA_MANAGEMENT,
                        },
                        {
                            label: "Vestigingsgegevens tonen",
                            icon: "Location",
                            link: RouteEnum.SHOW_ESTABLISHMENT_DETAILS,
                        },
                    ]}
                    location={props.location}
                    setLocation={props.setLocation}
                    isLoading={establishmentDataIsLoading}
                />
            )}
        </>
    );
};
